import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import Table from '../../../../../components/shared/Table';
import backend from '../../../../../services/backend';
import { useCertificados } from '../../../../../services/hooks/useCertificados';
import { useProdutoCert } from '../../../../../services/hooks/useProdutoCert';
import formatValue from '../../../../../utils/formatValue';
import { stepFaturaData } from '../DadosFatura';
import { stepPedidoData } from '../Pedido';

export const FormSchema = z.object({});

interface IDadosCertificado {
  handleMudaStep(novoStep: number): void;
  pedidoData: stepPedidoData | Record<string, unknown>;
  certificadoData: any;
  faturaData: stepFaturaData | Record<string, unknown>;
}

const Finalizar: React.FC<IDadosCertificado> = ({
  handleMudaStep,
  pedidoData,
  certificadoData,
  faturaData,
}) => {
  const Toast = useToast();
  const history = useHistory();
  const color = useColorModeValue('gray.800', 'white');
  const details = useColorModeValue('gray.200', 'gray.700');

  // estados
  const [enviando, setEnviando] = useState(false);

  // dados
  const { data: produtoData, isLoading, error } = useProdutoCert(pedidoData);
  const data = produtoData ?? [];

  const { refetch: RefetchCertificados } = useCertificados();

  type formData = z.infer<typeof FormSchema>;
  const methods = useForm<formData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      documento: '',
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const handleSalvaDados = async (data: formData) => {
    if (enviando) return;
    if (isLoading) return;
    setEnviando(true);

    const { arquivos } = certificadoData;

    try {
      const formData = new FormData();
      formData.append('produto', String(produtoData?.[0]?.id_produto) || '0');
      formData.append('tipo_lancamento', String(pedidoData.tipoPedido));
      formData.append('certificado', JSON.stringify(certificadoData || []));
      formData.append('faturamento', JSON.stringify(faturaData || []));

      const arquivosValidos = (arquivos || []).filter((file: any) => {
        return file instanceof File && file.size > 0;
      });

      if (arquivosValidos.length === 0) {
        throw new Error('Selecione ao menos um documento.');
      }

      if (arquivosValidos.length !== (arquivos || []).length) {
        throw new Error(
          'Um ou mais arquivos sao invalidos. Selecione os documentos novamente.',
        );
      }

      arquivosValidos.forEach((file: File) => {
        formData.append('arquivos[]', file, file.name);
      });

      const response = await backend.post('/certificado', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      Toast({
        title: 'Sucesso',
        isClosable: true,
        status: 'success',
        position: 'bottom-right',
      });
      RefetchCertificados();
      history.push(`/certificados/agendamento/${response.data.venda_uuid}`);
    } catch (error: any) {
      Toast({
        title: 'Problema ao finalizar certificado',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Erro ao finalizar certificado.',
        isClosable: true,
        status: 'error',
        position: 'bottom-right',
      });
    } finally {
      setEnviando(false);
    }
  };

  const colunas = React.useMemo(
    () => [
      {
        Header: 'produto',
        accessor: 'descricao',
      },
      {
        Header: 'valor tabela',
        accessor: 'valor_tabela',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.800', 'white');
          return (
            <>
              <Text color={color}>
                {formatValue(row.original.valor_tabela)}
              </Text>
            </>
          );
        },
      },
      {
        Header: 'acrescimo',
        accessor: 'acrescimo',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.800', 'white');
          return (
            <>
              <Text color={color}>{formatValue(row.original.acrescimo)}</Text>
            </>
          );
        },
      },
      {
        Header: 'desconto',
        accessor: 'desconto',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.800', 'white');
          return (
            <>
              <Text color={color}>{formatValue(row.original.desconto)}</Text>
            </>
          );
        },
      },
      {
        Header: 'comissÃ£o',
        accessor: 'comissao',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.800', 'white');
          return (
            <>
              <Text color={color}>{formatValue(row.original.comissao)}</Text>
            </>
          );
        },
      },
      {
        Header: 'total parcial',
        accessor: 'total_parcial',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.800', 'white');
          return (
            <>
              <Text color={color}>
                {formatValue(row.original.total_parcial)}
              </Text>
            </>
          );
        },
      },
    ],
    [],
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleSalvaDados, errors => {
          Toast({
            title: 'ValidaÃ§Ã£o',
            description: 'Preencha os campos destacados.',
            status: 'error',
            duration: 4000,
            position: 'bottom-right',
            isClosable: true,
          });
        })}
      >
        <Flex justify="space-between" mx="8">
          <Button onClick={() => handleMudaStep(3)}>Voltar</Button>
          <Button type="submit" disabled={isLoading || !produtoData}>
            {enviando ? (
              <>
                <Spinner mr="2" />
                Finalizando...
              </>
            ) : (
              'Finalizar Compra'
            )}
          </Button>
        </Flex>
        <Flex mt="4">
          <Box w="100%" mx="8px" borderWidth="1px" rounded="2xl">
            <Table
              data={data}
              columns={colunas}
              isLoading={isLoading}
              hideFooter
            />
          </Box>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default Finalizar;
